import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Send, Banknote, Smartphone, CircleCheck, Loader2 } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { QuoteSummary } from "@/components/QuoteSummary";
import { DateRangeField, nightsBetween } from "@/components/DateRangeField";
import { formatDate, euro } from "@/hooks/useQuote";

const inputCls =
  "block w-full min-w-0 min-h-[48px] rounded-xl border border-line bg-white px-4 py-3 text-sm normal-case tracking-normal text-ink outline-none transition-colors duration-300 focus:border-clay placeholder:text-fog/60";

export const BookingSection = ({ property, booking, setBooking, quoteState, onBooked, booked = [], bookedMatchers = [] }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [summary, setSummary] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const waMessage = () =>
    `Olá! Gostaria de pedir disponibilidade para o ${property?.name}.\n` +
    `Check-in: ${booking.checkIn || "a combinar"}\nCheck-out: ${booking.checkOut || "a combinar"}\n` +
    `Hóspedes: ${booking.guests}\nNome: ${form.name || ""}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("O nome é obrigatório.");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Indique o email ou o telemóvel.");
      return;
    }
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("O email não parece válido.");
      return;
    }
    if (!booking.checkIn || !booking.checkOut) {
      toast.error("Escolha as datas de check-in e check-out.");
      return;
    }
    if (booking.checkIn < today) {
      toast.error("A data de check-in não pode ser no passado.");
      return;
    }
    if (booking.checkOut <= booking.checkIn) {
      toast.error("O check-out tem de ser depois do check-in.");
      return;
    }
    if (quoteState?.quote && !quoteState.quote.available) {
      toast.error("Estas datas já estão reservadas. Escolha outras datas.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/bookings", {
        property_id: property.id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        check_in: booking.checkIn,
        check_out: booking.checkOut,
        guests: booking.guests,
        message: form.message,
      });
      setSummary(data);
      setDone(true);
      onBooked?.();
      toast.success("Pedido de reserva enviado! Respondemos em breve.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar o pedido. Tente pelo WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reservar" data-testid="booking-section" className="relative overflow-hidden bg-ink py-24 text-cream md:py-32">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-clay">Reservas</p>
          <h2 className="font-display text-4xl leading-tight sm:text-5xl lg:text-6xl">
            Peça a sua <em className="text-clay">estadia</em>.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70">
            Envie o pedido com as datas pretendidas — confirmamos a disponibilidade e combinamos
            o check-in e o check-out por telefone ou WhatsApp. {property?.price_note}, sem pagamento online.
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-sm text-cream/80">
              <Banknote size={17} className="text-clay" /> Pagamento por transferência bancária
            </div>
            <div className="flex items-center gap-3 text-sm text-cream/80">
              <Smartphone size={17} className="text-clay" /> Ou MB Way, como preferir
            </div>
            <a
              data-testid="whatsapp-cta-btn"
              href={waLink(property?.whatsapp, waMessage())}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-medium text-ink transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-[#1eb857]"
            >
              <MessageCircle size={17} /> Falar no WhatsApp
            </a>
          </div>

          <div className="mt-12">
            <h3 className="mb-4 font-display text-xl italic text-cream/90">Regras da casa</h3>
            <ul className="space-y-2.5">
              {(property?.rules || []).map((rule, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-cream/70" data-testid={`rule-${i}`}>
                  <CircleCheck size={15} className="mt-0.5 shrink-0 text-clay" /> {rule}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="h-fit rounded-2xl bg-cream p-6 text-ink shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] sm:p-8"
          data-testid="booking-form"
        >
          {done ? (
            <div className="flex flex-col items-center gap-4 py-10 text-center" data-testid="booking-success">
              <CircleCheck size={44} className="text-clay" />
              <h3 className="font-display text-2xl">Pedido recebido!</h3>
              <p className="max-w-xs text-sm text-fog">
                Obrigado, {summary?.name}! A Sandra vai contactá-lo para confirmar a reserva. Não é feito nenhum pagamento agora.
              </p>
              {summary && (
                <dl className="w-full max-w-sm divide-y divide-line rounded-xl border border-line bg-white text-left text-sm" data-testid="booking-success-summary">
                  {[
                    ["Entrada", formatDate(summary.check_in), "summary-checkin"],
                    ["Saída", formatDate(summary.check_out), "summary-checkout"],
                    ["Noites", summary.nights, "summary-nights"],
                    ["Hóspedes", summary.guests, "summary-guests"],
                    ["Total estimado", summary.total != null ? euro(summary.total) : "—", "summary-total"],
                  ].map(([k, v, id]) => (
                    <div key={id} className="flex justify-between px-4 py-2.5">
                      <dt className="text-fog">{k}</dt>
                      <dd data-testid={id} className="font-semibold text-ink">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <p className="text-xs text-fog" data-testid="booking-success-contacts">
                Dúvidas? <a href={`tel:+351${(property?.phone || "967338872").replace(/\D/g, "")}`} className="font-medium text-clay">{property?.phone || "967 338 872"}</a>
                {" · "}<a href={`mailto:${property?.email}`} className="font-medium text-clay">{property?.email}</a>
              </p>
              <button
                type="button"
                data-testid="booking-new-request-btn"
                onClick={() => setDone(false)}
                className="mt-2 rounded-full border border-ink/15 px-6 py-2.5 text-sm transition-colors duration-300 hover:bg-ink hover:text-cream"
              >
                Fazer outro pedido
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              <label className="flex min-w-0 flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
                <span>Nome <span className="text-clay">*</span></span>
                <input data-testid="booking-name-input" placeholder="O seu nome" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex min-w-0 flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
                  Email
                  <input data-testid="booking-email-input" type="email" placeholder="nome@exemplo.com" value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
                </label>
                <label className="flex min-w-0 flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
                  Telemóvel
                  <input data-testid="booking-phone-input" type="tel" placeholder="+351 9xx xxx xxx" value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
                </label>
              </div>
              <p className="-mt-2 text-xs text-fog" data-testid="booking-contact-hint">
                <span className="text-clay">*</span> Indique pelo menos o email ou o telemóvel. Com email, recebe uma confirmação do pedido.
              </p>
              <div data-testid="booking-form-dates-field">
                <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
                  Datas <span className="text-clay">*</span>
                </span>
                <div className="rounded-xl border border-line bg-white px-4 py-2.5 transition-colors duration-300 focus-within:border-clay hover:border-clay">
                  <DateRangeField
                    booked={booked}
                    bookedMatchers={bookedMatchers}
                    value={{ checkIn: booking.checkIn, checkOut: booking.checkOut }}
                    onChange={(range) => setBooking((b) => ({ ...b, ...range }))}
                    nights={nightsBetween(booking.checkIn, booking.checkOut)}
                    testId="form-dates"
                    triggerClassName="min-h-[42px]"
                  />
                </div>
              </div>
              <label className="flex min-w-0 flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
                Hóspedes
                <select data-testid="booking-form-guests" value={booking.guests}
                  onChange={(e) => setBooking((b) => ({ ...b, guests: Number(e.target.value) }))} className={inputCls}>
                  {Array.from({ length: property?.max_guests || 6 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n} {n === 1 ? "hóspede" : "hóspedes"}</option>
                  ))}
                </select>
              </label>
              <QuoteSummary {...quoteState} testId="form-quote" />
              <textarea data-testid="booking-message-input" rows={3} placeholder="Notas (opcional)"
                value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className={`${inputCls} resize-none`} />
              <button
                data-testid="booking-submit-btn"
                type="submit"
                disabled={sending}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-clay px-8 py-4 text-sm font-medium text-cream transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-clay-dark disabled:opacity-60"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? "A enviar..." : "Enviar pedido de reserva"}
              </button>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
};
